import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Search, 
  BarChart3, 
  FileText, 
  Users, 
  Zap, 
  ShieldCheck,
  ArrowRight,
  CheckCircle2
} from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Search,
      title: "AI Query Simulation",
      description: "See exactly what happens when prospects ask AI assistants for the best local service providers."
    },
    {
      icon: Users,
      title: "Competitor Analysis",
      description: "Discover who's being recommended instead of you and understand why they're more visible."
    },
    {
      icon: BarChart3,
      title: "Gap Analysis",
      description: "Get actionable insights on what's missing from your digital presence."
    },
    {
      icon: FileText,
      title: "Shareable Report",
      description: "Download a professional PDF report you can share with your team or marketing agency."
    }
  ];

  const benefits = [
    "Understand your AI visibility in under 60 seconds",
    "See exactly who AI recommends over you",
    "Get prioritized action items to improve",
    "No technical knowledge required"
  ];

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#0a2540] flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#d4af37]" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#0a2540]" data-testid="brand-logo">
                PromptReady
              </span>
            </div>
            <Button 
              onClick={() => navigate("/audit")}
              className="bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] font-semibold rounded-full px-6"
              data-testid="header-cta-btn"
            >
              Start Free Audit
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a2540 0%, #162536 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight animate-fade-in-up"
              data-testid="hero-headline"
            >
              Is AI Recommending{" "}
              <span className="text-[#d4af37]">Your Competitors</span>{" "}
              Instead of You?
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl mx-auto animate-fade-in-up animate-delay-100">
              When prospects ask ChatGPT or Google AI "Who's the best {`{service}`} in {`{your town}`}?" — are you showing up? Find out in 60 seconds.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animate-delay-200">
              <Button 
                onClick={() => navigate("/audit")}
                className="bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] text-lg font-semibold rounded-full px-8 py-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                data-testid="hero-primary-cta-btn"
              >
                Run Your Free AI Audit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                onClick={() => navigate("/sample")}
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 text-lg rounded-full px-8 py-6"
                data-testid="hero-secondary-cta-btn"
              >
                See Sample Report
              </Button>
            </div>
            <div className="mt-8 flex items-center justify-center gap-6 text-sm text-slate-400 animate-fade-in-up animate-delay-300">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#3ab046]" />
                Free instant analysis
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#3ab046]" />
                No signup required
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#3ab046]" />
                PDF report included
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-sm font-semibold tracking-wider uppercase text-[#d4af37]">The New Reality</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-[#0a2540]">
              AI is Changing How Customers Find Local Services
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              More people are asking AI assistants for recommendations instead of scrolling through Google results. 
              If you're not visible to these AI systems, you're invisible to a growing segment of your market.
            </p>
          </div>
          <div className="mt-16 grid md:grid-cols-3 gap-8">
            <Card className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(10,37,64,0.08)] card-hover">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-[#0a2540]">73%</div>
                <p className="mt-2 text-slate-600">of consumers trust AI recommendations for local services</p>
              </CardContent>
            </Card>
            <Card className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(10,37,64,0.08)] card-hover">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-[#0a2540]">2.5x</div>
                <p className="mt-2 text-slate-600">more likely to choose an AI-recommended business</p>
              </CardContent>
            </Card>
            <Card className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(10,37,64,0.08)] card-hover">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-[#d41414]">89%</div>
                <p className="mt-2 text-slate-600">of local businesses don't know their AI visibility status</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-28 bg-[#f8f9fa]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold tracking-wider uppercase text-[#d4af37]">How It Works</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-[#0a2540]">
              Your Complete AI Visibility Report
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              Enter your business details and get instant insights into your AI visibility landscape.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(10,37,64,0.08)] card-hover"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-[#0a2540] flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-[#d4af37]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0a2540] mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-28 bg-[#0a2540] network-bg noise-overlay relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-semibold tracking-wider uppercase text-[#d4af37]">Why PromptReady</span>
              <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-white">
                Stop Guessing. Start Knowing.
              </h2>
              <p className="mt-6 text-lg text-slate-300">
                Most businesses have no idea whether AI assistants recommend them or their competitors. 
                Our audit gives you the clarity you need to take action.
              </p>
              <ul className="mt-8 space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3 text-white">
                    <div className="w-6 h-6 rounded-full bg-[#d4af37] flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-[#0a2540]" />
                    </div>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate("/audit")}
                className="mt-10 bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] text-lg font-semibold rounded-full px-8 py-6"
                data-testid="benefits-cta-btn"
              >
                Check Your Visibility Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#d41414]/20 text-[#ff6b6b] rounded-full text-sm font-medium">
                    <span className="w-2 h-2 rounded-full bg-[#d41414] animate-pulse" />
                    Visibility Status: Not Found
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-white/5 rounded-lg p-4">
                    <div className="text-xs text-slate-400 uppercase mb-1">Query Analyzed</div>
                    <div className="text-white font-mono">"best roofing company in Manchester"</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <div className="text-xs text-slate-400 uppercase mb-2">AI is Recommending Instead:</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between text-white">
                        <span>1. Manchester Roofing Pro</span>
                        <span className="text-[#3ab046]">Visible</span>
                      </div>
                      <div className="flex items-center justify-between text-white">
                        <span>2. Elite Roof Solutions</span>
                        <span className="text-[#3ab046]">Visible</span>
                      </div>
                      <div className="flex items-center justify-between text-white">
                        <span>3. Premier Roofing Co</span>
                        <span className="text-[#3ab046]">Visible</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#d4af37]/10 text-[#b5952f] rounded-full text-sm font-medium mb-6">
            <Zap className="w-4 h-4" />
            Takes less than 60 seconds
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0a2540]">
            Ready to See Your AI Visibility?
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            Enter your business details and discover whether AI assistants are recommending 
            you — or sending customers to your competitors.
          </p>
          <Button 
            onClick={() => navigate("/audit")}
            className="mt-8 bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] text-lg font-semibold rounded-full px-10 py-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
            data-testid="final-cta-btn"
          >
            Run Your Free AI Audit
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0a2540] border-t border-[#162536]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#162536] flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#d4af37]" />
              </div>
              <span className="font-heading text-xl font-semibold text-white">PromptReady</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <span className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                Your data is never shared
              </span>
            </div>
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} PromptReady.io. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
